import { layouts as photoBookLayouts } from '../../components/PhotoBook/layouts'; // to avoid conflict with any local 'layouts' variable
import axios from "axios";
import { create_photo_book } from '../../components/PhotoBook';
import { getDriveDisplayUrl } from '../../utils/driveUrls';
import { 
  cleanEntryForAI, 
  createEnhancedLayoutsForAI
} from './aiHelpers';

// Sleep function (can be moved to a general utils if used elsewhere)
const sleep = ms => new Promise(res => setTimeout(res, ms));

// Helper function to format date (internal to this module now)
const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const processEntryForStoryBookInternal = (entry, idCounter, photoMapRef) => {
  if (!entry) {
    return null;
  }
  const processedPhotos = entry.photos ? entry.photos.map(photo => {
    const photoId = idCounter.current;
    idCounter.current += 1;
    
    // Start with a copy of the original photo object
    let processedPhoto = { ...photo };
    
    // Calculate the URL based on whether it's a drive file or Google Photos file
    let finalBaseUrl;
    if (photo.driveFileId) {
      finalBaseUrl = getDriveDisplayUrl(photo.driveFileId);
    } else {
      finalBaseUrl = photo.baseUrl ? `${photo.baseUrl}=d` : '';
      if (!photo.baseUrl) {
        console.warn(`BaseUrl missing for Google Photo: ${photo.filename || photo.id}. Storybook display might fail.`);
      }
    }
    
    // Calculate photo size if mediaMetadata is available
    let photoSize = "0x0";
    if (photo.mediaMetadata && photo.mediaMetadata.width && photo.mediaMetadata.height) {
      photoSize = `${photo.mediaMetadata.width}x${photo.mediaMetadata.height}`;
    }
    
    // Store the URL in photoMapRef for reference
    photoMapRef.current[photoId] = finalBaseUrl;
    
    // Add/modify only the necessary properties
    processedPhoto = {
      ...processedPhoto,
      id: photoId,
      ai_reference_id: photoId,
      mediaId: photo.driveFileId || photo.id, // Persistent identifier
      isDriveFile: !!photo.driveFileId,
      journalEntryId: entry.id,
      original_photo_id: photo.id,
      size: photoSize,
      url: finalBaseUrl,
      // Set defaults for optional properties
      place: processedPhoto.place || null,
      caption: processedPhoto.caption || '',
      story_caption: processedPhoto.story_caption || '',
      story_caption_score: processedPhoto.story_caption_score || 0,
      date_taken: processedPhoto.date_taken || null,
      // Default crop info
      cropInfo: { zoom: 1, crop: { x: 0, y: 0 }, croppedAreaPercentages: null },
    };
    
    return processedPhoto;
  }) : [];

  const { userId, createdAt, aiRewrite, ...restOfEntry } = entry;
  const formattedCreatedAt = entry.createdAt ? formatDate(entry.createdAt.seconds * 1000) : formatDate(new Date());

  return {
    createdAt: formattedCreatedAt,
    id: entry.id, // Always include the entry ID
    ...restOfEntry,
    photos: processedPhotos
  };
};


// Helper function to adjust font size to fit text within a box
function getAdjustedFontSize(text, textStyle, pageContainerWidthPx, pageContainerHeightPx, logFn) {
  let initialFontSize = 16; // Default font size
  if (textStyle && textStyle.fontSize) {
    const parsedSize = parseFloat(textStyle.fontSize);
    if (!isNaN(parsedSize)) {
      initialFontSize = parsedSize;
    } else {
      if (logFn) logFn("Text fitting: textStyle.fontSize is invalid, using default", { fontSize: textStyle.fontSize });
    }
  } else if (textStyle) { // textStyle exists but fontSize might be missing
     if (logFn) logFn("Text fitting: textStyle.fontSize not provided, using default");
  } else { // textStyle itself is missing
    if (logFn) logFn("Text fitting: textStyle not provided, using default initial font size and returning it.");
    return initialFontSize; // Cannot proceed without textStyle for dimensions
  }
  
  if (!text) { // If no text, no adjustment needed, return initial or default
    if (logFn) logFn("Text fitting: No text provided, returning initial font size.");
    return initialFontSize;
  }

  let currentFontSize = initialFontSize;
  const minFontSize = 8; // Minimum readable font size
  const step = 0.5; // Font size reduction step

  const tempDiv = document.createElement('div');
  document.body.appendChild(tempDiv);

  tempDiv.style.position = 'absolute';
  tempDiv.style.visibility = 'hidden';
  tempDiv.style.fontFamily = textStyle.fontFamily || 'Arial';
  tempDiv.style.lineHeight = textStyle.lineHeight || 'normal';
  tempDiv.style.padding = textStyle.padding || '0px'; // General padding
  tempDiv.style.wordBreak = textStyle.wordBreak || 'break-word';

  let boxWidthPx = 0;
  if (textStyle.width) {
    if (String(textStyle.width).endsWith('%')) {
      boxWidthPx = (parseFloat(textStyle.width) / 100) * pageContainerWidthPx;
    } else if (String(textStyle.width).endsWith('px')) {
      boxWidthPx = parseFloat(textStyle.width);
    } else {
      boxWidthPx = pageContainerWidthPx; 
    }
  } else {
    boxWidthPx = pageContainerWidthPx; 
  }

  const paddingLeft = parseFloat(textStyle.paddingLeft || textStyle.padding || '0');
  const paddingRight = parseFloat(textStyle.paddingRight || textStyle.padding || '0');
  const availableWidth = boxWidthPx - paddingLeft - paddingRight;

  let boxHeightPx = 0;
  if (textStyle.height) {
    if (String(textStyle.height).endsWith('%')) {
      boxHeightPx = (parseFloat(textStyle.height) / 100) * pageContainerHeightPx;
    } else if (String(textStyle.height).endsWith('px')) {
      boxHeightPx = parseFloat(textStyle.height);
    } else {
      boxHeightPx = pageContainerHeightPx; 
    }
  } else {
    boxHeightPx = pageContainerHeightPx; 
  }
  const paddingTop = parseFloat(textStyle.paddingTop || textStyle.padding || '0');
  const paddingBottom = parseFloat(textStyle.paddingBottom || textStyle.padding || '0');
  const availableHeight = boxHeightPx - paddingTop - paddingBottom;

  if (availableWidth <= 0 || availableHeight <= 0) {
    if (logFn) logFn("Text fitting: Invalid text box dimensions", { availableWidth, availableHeight, textStyle });
    if (tempDiv.parentNode === document.body) document.body.removeChild(tempDiv); // Ensure removal
    return initialFontSize;
  }
  
  tempDiv.style.width = `${availableWidth}px`;
  tempDiv.innerText = text;

  let iterations = 0;
  const maxIterations = (initialFontSize - minFontSize) / step + 5; 

  while (currentFontSize >= minFontSize && iterations < maxIterations) {
    tempDiv.style.fontSize = `${currentFontSize}px`;
    if (tempDiv.scrollHeight <= availableHeight) { //&& tempDiv.scrollWidth <= availableWidth) {
      break; 
    }
    currentFontSize -= step;
    iterations++;
  }
  
  const finalSize = Math.max(currentFontSize, minFontSize);

  if (finalSize !== initialFontSize && logFn) {
    logFn("Text fitting: Font size adjusted", { 
      initialFontSize, 
      newFontSize: finalSize, 
      iterations, 
      textLength: text.length, 
      textPreview: text.length > 50 ? text.substring(0, 50) + '...' : text,
      availableWidth, 
      availableHeight,
      textOverflow: tempDiv.scrollHeight > availableHeight || tempDiv.scrollWidth > availableWidth
    });
  } else if (logFn) {
    logFn("Text fitting: Font size not adjusted or remained same", { 
      initialFontSize, 
      finalSize, 
      iterations, 
      textLength: text.length,
      textPreview: text.length > 50 ? text.substring(0, 50) + '...' : text,
      availableWidth, 
      availableHeight 
    });
  }
  
  if (tempDiv.parentNode === document.body) document.body.removeChild(tempDiv); // Ensure removal
  return finalSize;
}

export const generateStoryBookLayoutPlanInternal = async (
    entry,
    priorGeneratedTexts,
    chatGptToken,
    debugMode,
    userComment,
    photoMapRef, // Pass the ref object
    logAIFn,
    logLayoutFn,
    spreadPreviewWidthPx, 
    spreadPreviewHeightPx
) => {
  if (logAIFn) logAIFn('Generating story book layout plan', { entryDate: entry.createdAt, photoCount: entry.photos.length });
  
  // Clean entry data to reduce noise in the AI prompt
  const cleanedEntry = cleanEntryForAI(entry, logAIFn);
  
  // Create enhanced layouts with AI hints
  const enhancedLayouts = createEnhancedLayoutsForAI(photoBookLayouts, logLayoutFn);

  const singlePagePreviewWidthPx = spreadPreviewWidthPx / 2;
  const singlePagePreviewHeightPx = spreadPreviewHeightPx;
  if (logLayoutFn) logLayoutFn('Single page dimensions for text fitting based on spread preview', { singlePagePreviewWidthPx, singlePagePreviewHeightPx });
  
  const prompt = `
    You are a professional photo book editor. Below are the available layouts with detailed descriptions (including photo counts and estimated text word capacities per page: 'leftPageMaxWords' and 'rightPageMaxWords') and the current story entry.

    Available Layouts:
    ${JSON.stringify(enhancedLayouts, null, 2)}

    Prior Generated Texts:
    ${JSON.stringify(priorGeneratedTexts, null, 2)}

    Current Story Entry (cleaned for relevance):
    ${JSON.stringify(cleanedEntry, null, 2)}

    Your task is to create a beautiful family photo book integrating the story and photos.
    Each layout has two pages: left and right. Choose the best layout that fits the experience.
    
    Read the aiHint for each layout. Pay special attention to 'leftPageMaxWords' and 'rightPageMaxWords' to understand text capacity.
    - Select layouts with appropriate text box sizes. Avoid large text boxes if the story part is short. Do not choose layouts with text boxes if there's no story to tell for that part.
    - For any text you generate, aim for at least 75% of the respective page's 'maxWords' capacity if a text box exists on that page.
    - Prefer photos with people and higher story_caption_score (>= 65 are interesting). Ensure these are included.
    - The number of photos picked must exactly match 'totalPhotos' for the chosen layout.
    - If an experience or story spans multiple days or distinct sub-topics within the same entry, try to use a layout that has text areas on *both* pages. This allows you to dedicate each page (left/right) to a specific day or sub-topic, improving clarity. For such cases, provide distinct text, date, and city for both left and right pages.
    CHOOSE UP TO 2 LAYOUTS UNLESS THERE ARE PHOTOS FROM MULTIPLE DAYS IN WHICH CASE YOU MAY CHOOSE 1, 2 or 3 LAYOUTS. FOR MULTIPLE DAYS CHOOSE ST LEAST ONE PHOTO PER DAY.  
    ALWAYS PROVIDE THE NUMBER OF PHOTOS TO USE ALL PLACES IN THE LAYOUTS.  
    DO NOT USE THE SAME PHOTO TWICE. PREFER PHOTOS WITH HIGHER story_caption_score. PREFER PHOTOS WITH MORE THAN ONE PERSON. IF A PAGE HAS ONE PHOTO, PREFER THE HIGHEST story_caption_score PHOTO.
    THE STORY TEXTS MUST BE IN FIRST PERSON AND SHOULD BE FIRST BASED ON THE ENTRY TEXTS AND THEN ON PHOTOS. DO NOT TRY TO COME UP WITH A STORY TEXT THAT IS NOT BASED ON THE ENTRY TEXTS OR VISIBLE IN THE PHOTOS.

    For each chosen layout, specify:
    1.  layout_id: The ID of the chosen layout.
    2.  selected_photo_ids: Array of photo IDs (integers from 'id' field of photos in Current Story Entry). First N photos for left page, rest for right, matching layout's photo counts.
    3.  left_page_text: Narrative for the left page. If the chosen layout's left page has a text box (leftPageMaxWords > 0), generate text aiming for at least 75% of its capacity. If no text box on left, or no relevant story, this should be an empty string.
    4.  right_page_text: Narrative for the right page. If the chosen layout's right page has a text box (rightPageMaxWords > 0), generate text aiming for at least 75% of its capacity. If no text box on right, or no relevant story, this should be an empty string.
    5.  left_page_display_date: "DD/MM (DAY)" for left page content. Infer from photo dates_taken.
    6.  left_page_display_city: "City Name" for left page content. Extract from photo place.address.
    7.  right_page_display_date: "DD/MM (DAY)" for right page content.
    8.  right_page_display_city: "City Name" for right page content.
    (If content on a spread is unified (e.g., single event, single day), left and right page dates/cities can be the same. If different, make them distinct.)
    include your full chain-of-thought right in the response in the 'explanation' field. Do not hide your reasoning. show each step you take.
    ${userComment ? `User's comment: "${userComment}"` : ""}
    
    Return the result in JSON format with the structure:
    {
      "photoStoryBook": [
        { 
          "layout_id": 1, // Example: number
          "selected_photo_ids": [101, 102, 103], // Example: array of numbers
          "left_page_text": "A wonderful day exploring Tokyo's landmarks.", // Example
          "right_page_text": "The beauty of Kyoto's temples.", // Example
          "left_page_display_date": "25/12 (Tue)" // Example,
          "left_page_display_city": "Tokyo" // Example,
          "right_page_display_date": "26/12 (Wed)" // Example,
          "right_page_display_city": "Kyoto" // Example,
          "explanation": "string",
        }
      ]
    }
  `;
  if (logAIFn) {
    // Get a truncated version of the prompt for logging (first 300 chars)
    const truncatedPrompt = prompt.length > 300 
      ? prompt.substring(0, 300) + '...' 
      : prompt;

    logAIFn('Calling OpenAI API for layout plan', { 
      type: 'AI_REQUEST',
      requestTime: new Date().toISOString(),
      model: "o4-mini",
      debugMode, 
      promptLength: prompt.length,
      promptPreview: truncatedPrompt,
      enhancedLayoutCount: enhancedLayouts.length,
      photoCount: cleanedEntry.photos.length,
      // Store full prompt in a separate field for popup viewing
      fullPrompt: prompt
    });
  }
  let response = null;
  const requestBody = {
    model: "o4-mini", // TODO: making this configurable
    response_format: { "type": "json_object" },
    messages: [
      { role: "system", content: "You are a helpful assistant that creates layout plans for a photo storybook, designed to output JSON." },
      { role: "user", content: prompt },
    ],
    reasoning_effort: "medium",
    max_completion_tokens: 12000, // max_tokens for potentially larger responses
  };
  const config = { headers: { Authorization: `Bearer ${chatGptToken}` } };
  let retries = 5;
  while (retries > 0) {
    retries--;
    try {
      response = await axios.post("https://api.openai.com/v1/chat/completions", requestBody, config);
      if (logAIFn) {
        const responseContent = response.data?.choices[0]?.message?.content;
        const responseTime = new Date().toISOString();
        
        // Log more detailed response information
        logAIFn('Received response from OpenAI API', { 
          type: 'AI_RESPONSE',
          responseTime,
          responseDataLength: responseContent?.length, 
          promptTokenCount: response.data?.usage?.prompt_tokens || 'unknown',
          completionTokenCount: response.data?.usage?.completion_tokens || 'unknown',
          totalTokenCount: response.data?.usage?.total_tokens || 'unknown',
          model: response.data?.model || 'gpt-4.1',
          response: responseContent
        });
      }
      break;
    } catch (error) {
      let rateLimit = false;
      let waitMs = 3000;
      const status = error.response?.status;
      const headers = error.response?.headers || {};
      const bodyErr = error.response?.data?.error;
      if (status === 429) {
        rateLimit = true;
        const resetHdr = headers['x-ratelimit-reset-requests'];
        if (typeof resetHdr === 'string') {
          const m = resetHdr.match(/(\d+(?:\.\d+)?)(ms|s)/);
          if (m) waitMs = m[2] === 's' ? parseFloat(m[1]) * 1000 : parseFloat(m[1]);
        }
      } else if (bodyErr?.code === 'rate_limit_exceeded' || (typeof bodyErr?.message === 'string' && bodyErr.message.includes('quota'))) {
        rateLimit = true;
        const msg = bodyErr.message || '';
        const m = msg.match(/Please try again in (\d+(?:\.\d+)?)(m|s|ms)\./) || msg.match(/Please try again in (\d+)s\./) ;
        if (m) {
            const val = parseFloat(m[1]);
            if (m[2] === 'ms') waitMs = val;
            else if (m[2] === 'm') waitMs = val * 60 * 1000;
            else waitMs = val * 1000; // 's' or default to seconds
        } else if (msg.includes("quota")) {
            console.error("OpenAI quota exceeded:", msg);
            throw new Error(`OpenAI Quota Exceeded: ${msg}`); // Re-throw specific error for quota
        }
      }
      if (rateLimit) {
        waitMs = Math.min(Math.max(waitMs, 1000), 20000); // Max wait 20s
        console.warn(`OpenAI rate limited or server busy, retrying in ${waitMs}ms`);
        if (logAIFn) logAIFn('OpenAI rate limited/busy', { 
          type: 'AI_ERROR',
          errorType: 'RATE_LIMIT',
          waitMs,
          retryCount: 5 - retries,
          timestamp: new Date().toISOString()
        });
        await sleep(waitMs);
        continue;
      } else {
        console.error("Error calling OpenAI API:", error.response?.data || error.message);
        if (logAIFn) logAIFn('OpenAI API call error', { 
          type: 'AI_ERROR',
          errorType: 'API_ERROR',
          status: error.response?.status || 'unknown',
          message: error.message,
          responseData: error.response?.data,
          timestamp: new Date().toISOString()
        });
        throw error; // Re-throw other errors
      }
    }
  }

  if (!response) {
    throw new Error("Failed to get a response from OpenAI API after multiple retries.");
  }

  // Check if response has the expected structure
  if (!response.data || !response.data.choices || !response.data.choices[0] || !response.data.choices[0].message) {
    throw new Error('Invalid OpenAI response structure');
  }
  
  const messageContent = response.data.choices[0].message.content;
  if (!messageContent) {
    throw new Error('Empty message content from OpenAI');
  }

  const layoutPlanResponse = JSON.parse(messageContent);
  
  // Check if the response has the expected structure
  if (!layoutPlanResponse.photoStoryBook || !Array.isArray(layoutPlanResponse.photoStoryBook)) {
    if (logAIFn) logAIFn('OpenAI response missing expected photoStoryBook array', { 
      type: 'AI_ERROR',
      responseStructure: Object.keys(layoutPlanResponse),
      fullResponse: layoutPlanResponse
    });
    
    // Try to find any array in the response that might contain our layout data
    let layoutArray = null;
    for (const key in layoutPlanResponse) {
      if (Array.isArray(layoutPlanResponse[key]) && 
          layoutPlanResponse[key].length > 0 && 
          layoutPlanResponse[key][0].layout_id) {
        layoutArray = layoutPlanResponse[key];
        if (logAIFn) logAIFn('Found alternative layout array', { key, length: layoutArray.length });
        break;
      }
    }
    
    // If no suitable array found, create an empty one to avoid errors
    if (!layoutArray) {
      console.error('Response does not contain required photoStoryBook array:', layoutPlanResponse);
      throw new Error('AI response missing required photoStoryBook array structure');
    }
    
    // Use the found array as our photoStoryBook
    layoutPlanResponse.photoStoryBook = layoutArray;
  }
  
  if (logAIFn) logAIFn('Processing layout plan response', { 
    type: 'AI_RESULT_PROCESSING',
    timestamp: new Date().toISOString(),
    layoutCount: layoutPlanResponse.photoStoryBook?.length || 0,
    layoutIds: layoutPlanResponse.photoStoryBook?.map(layout => layout.layout_id) || [],
    totalPhotosUsed: layoutPlanResponse.photoStoryBook?.reduce(
      (sum, layout) => sum + (layout.selected_photo_ids?.length || 0), 0
    ),
    fullLayoutPlan: layoutPlanResponse
  });
  
  const updatedLayoutPlan = layoutPlanResponse.photoStoryBook.map(layoutEntry => {
    const photoIds = layoutEntry.selected_photo_ids || [];
    
    // Find the complete photo objects from original entry based on ID
    const photoObjects = photoIds.map(id => {
      // Find the photo object from the original entry's photos array
      const photoObject = entry.photos.find(photo => photo.id === id || photo.ai_reference_id === id);
      if (!photoObject) {
        console.warn(`Photo with ID ${id} not found in original entry photos.`);
        if (logAIFn) logAIFn('Photo lookup failed for layout plan', {
          type: 'DATA_ERROR',
          missingPhotoId: id,
          availablePhotoIdsInEntry: entry.photos.map(p => p.id)
        });
        return null;
      }
      // Ensure cropInfo defaults if somehow missing
      return {
        ...photoObject,
        cropInfo: photoObject.cropInfo || { zoom: 1, crop: { x: 0, y: 0 }, croppedAreaPercentages: null }
      };
    }).filter(Boolean); // Remove any null entries

    // Get URLs from the photo objects or photoMapRef as fallback
    const photoUrls = photoObjects.map(photoObj => photoObj ? photoObj.url : null)
      .filter(url => url && url !== 'undefined' && !url.endsWith('undefined'));

    if (photoUrls.length !== photoIds.length) {
      console.warn("Some photo URLs were invalid or undefined and excluded from layout plan:", { layoutEntry, photoIds, photoObjects });
      if (logAIFn) logAIFn('Invalid photo URLs in layout plan', { 
        badPhotoIds: photoIds.filter(id => {
          const photo = entry.photos.find(p => p.id === id || p.ai_reference_id === id);
          return !photo || !photo.url || photo.url === 'undefined' || photo.url.endsWith('undefined');
        }),
        layoutDetails: layoutEntry
      });
    }

    // Process text content and calculate font sizes
    const leftPageText = layoutEntry.left_page_text || "";
    const rightPageText = layoutEntry.right_page_text || "";
    let adjustedLeftPageFontSize, adjustedRightPageFontSize;
    
    // Get the layout definition to access style information
    const layoutDef = photoBookLayouts.find(l => l.id === Number(layoutEntry.layout_id));
    
    // Handle left page text
    if (layoutDef && layoutDef.leftPage && layoutDef.leftPage.text && 
        layoutDef.leftPage.text.style && leftPageText) {
      const targetTextStyle = layoutDef.leftPage.text.style;
      adjustedLeftPageFontSize = parseFloat(targetTextStyle?.fontSize) || 16;
      if (logAIFn) logAIFn('Adjusting font size for LEFT page text', { 
        layoutId: layoutEntry.layout_id, 
        textLength: leftPageText.length, 
        originalFontSize: targetTextStyle.fontSize, 
        containerWidth: singlePagePreviewWidthPx, 
        containerHeight: singlePagePreviewHeightPx 
      });
      adjustedLeftPageFontSize = getAdjustedFontSize(
        leftPageText, 
        targetTextStyle, 
        singlePagePreviewWidthPx, 
        singlePagePreviewHeightPx, 
        logAIFn
      );
    }
    
    // Handle right page text
    if (layoutDef && layoutDef.rightPage && layoutDef.rightPage.text && 
        layoutDef.rightPage.text.style && rightPageText) {
      const targetTextStyle = layoutDef.rightPage.text.style;
      adjustedRightPageFontSize = parseFloat(targetTextStyle?.fontSize) || 16;
      if (logAIFn) logAIFn('Adjusting font size for RIGHT page text', { 
        layoutId: layoutEntry.layout_id, 
        textLength: rightPageText.length, 
        originalFontSize: targetTextStyle.fontSize, 
        containerWidth: singlePagePreviewWidthPx, 
        containerHeight: singlePagePreviewHeightPx 
      });
      adjustedRightPageFontSize = getAdjustedFontSize(
        rightPageText, 
        targetTextStyle, 
        singlePagePreviewWidthPx, 
        singlePagePreviewHeightPx, 
        logAIFn
      );
    }

    // Derive photo_place_names from the photoObjects
    const derivedPhotoPlaceNames = photoObjects.map(photo => 
        photo && photo.place && photo.place.name ? photo.place.name : ''
    );

    // Initialize placeholderModifications
    let numPlaceholders = 0;
    if (layoutDef) {
        numPlaceholders += layoutDef.leftPage?.photoPlaceholders?.length || 0;
        numPlaceholders += layoutDef.rightPage?.photoPlaceholders?.length || 0;
    }
    const initialPlaceholderModifications = numPlaceholders > 0 ? Array(numPlaceholders).fill(null) : [];

    return {
      layout_id: Number(layoutEntry.layout_id),
      photos: photoObjects,
      left_page_text: leftPageText,
      right_page_text: rightPageText,
      left_page_display_date: layoutEntry.left_page_display_date || '',
      left_page_display_city: layoutEntry.left_page_display_city || '',
      right_page_display_date: layoutEntry.right_page_display_date || '',
      right_page_display_city: layoutEntry.right_page_display_city || '',
      photo_place_names: derivedPhotoPlaceNames,
      explanation: layoutEntry.explanation || '',
      adjusted_left_page_font_size: adjustedLeftPageFontSize,
      adjusted_right_page_font_size: adjustedRightPageFontSize,
      placeholderModifications: initialPlaceholderModifications,
      // No longer generating legacy fields as we only support version 2.1 and above
    };
  });
  const generatedText = layoutPlanResponse.photoStoryBook.map(layout => 
    `${layout.left_page_text || ""} ${layout.right_page_text || ""}`.trim()
  ).join(' ');
  return { layoutPlan: updatedLayoutPlan, generatedText: generatedText };
};

export const renderStoryBookWithLayout = (layoutPlan, logLayoutFn, spreadPreviewWidthUsedForFitting, onAlbumLoadedCallback, onRequestEditBatch, onLayoutPlanUpdateCallback, onPhotoCropChangeInPlanCallback, albumId) => {
  if (logLayoutFn) logLayoutFn('Starting story book rendering with layout plan', { 
    layoutCount: layoutPlan.length,
    layoutSummary: layoutPlan.map((layout, idx) => ({
      index: idx + 1,
      layoutId: layout.layout_id,
      photoCount: layout.photos.length,
    }))
  });
  
  const book = create_photo_book();
  layoutPlan.forEach((layoutPlanEntry, idx) => {
    const { 
      layout_id, photos, 
      left_page_text, right_page_text,
      left_page_display_date, left_page_display_city,
      right_page_display_date, right_page_display_city,
      photo_place_names, explanation, 
      adjusted_left_page_font_size, adjusted_right_page_font_size,
      leftPageData, rightPageData,
      ai_batch_id, source_entry_ids, // Destructure batch ID and source IDs
      placeholderModifications // Destructure placeholder modifications
    } = layoutPlanEntry;

    if (logLayoutFn) {
        logLayoutFn('Adding page to photo book from layoutPlanEntry', {
            layoutId: layout_id,
            pageIndex: idx + 1,
            photoCount: photos.length,
            leftTextLength: left_page_text?.length || 0,
            rightTextLength: right_page_text?.length || 0,
            leftDate: left_page_display_date,
            rightDate: right_page_display_date,
            leftAdjFontSize: adjusted_left_page_font_size,
            rightAdjFontSize: adjusted_right_page_font_size,
        });
    }
    
    // Extract photo URLs for backward compatibility
    let photoData = photos;
    if (photos.length > 0 && typeof photos[0] === 'object' && photos[0] !== null) {
      photoData = photos;
    }
    
    book.addPage(
      layout_id, 
      photoData, 
      left_page_text, right_page_text,
      left_page_display_date, left_page_display_city,
      right_page_display_date, right_page_display_city,
      photo_place_names, 
      explanation, 
      adjusted_left_page_font_size, adjusted_right_page_font_size,
      leftPageData, rightPageData,
      ai_batch_id, source_entry_ids, // Pass batch ID then source IDs
      placeholderModifications // Pass placeholder modifications
    );
  });
  return book.render(spreadPreviewWidthUsedForFitting, onAlbumLoadedCallback, onRequestEditBatch, onLayoutPlanUpdateCallback, onPhotoCropChangeInPlanCallback, albumId); // Pass onPhotoCropChangeInPlanCallback
};
